import React, { useEffect, useMemo, useState } from 'react';
import entityMktSettingsService from '../../services/partnerMktSettingsService';
import IssuedValue from './IssuedValue';
import CredentialTest from './CredentialTest';
import './CredentialsPicker.css';
import { apiErrorMessage } from '../../utils/apiError';

const ENTITY_LABEL = { partner: 'Partner', account: 'Chain', hotel: 'Hotel' };

// Single source of truth credential picker used by sync, payment porter and vault.
//
// Props:
//   marketplaceId   — required
//   context, contextId — sync|pp|vault + record id, OR pass hotelId/accountId/partnerId directly
//   hotelId, accountId, partnerId — project's hierarchy (used when no contextId yet, e.g. vault create)
//   selection       — { credential_id } | null  (matched by entity_credentials.id FK)
//   onSelect(sel)   — fired when user picks an existing credential (sel = picker row, with share metadata)
//   onCreateAndShare(payload) — fired after a successful inline create-and-share; payload includes shareId, credential_id
//   allowAddAt      — array subset of ['hotel','account','partner']; restricts levels the user can add at
//   onClose         — optional, if rendered inside a modal
export default function CredentialsPicker({
    marketplaceId,
    context, contextId,
    hotelId, accountId, partnerId,
    selection,
    onSelect,
    onCreateAndShare,
    allowAddAt = ['hotel', 'account', 'partner'],
    requiresShareId = false,   // sync needs a real share row id to bind to the connector
    tabbed = false,            // render Partner / Chain / Hotel as tabs (full-screen sync picker)
    canEdit = false,           // superadmin only — show an Edit button on each credential row
}) {
    const [activeTab, setActiveTab]       = useState('hotel');     // tabbed layout only
    const [loading, setLoading]           = useState(false);
    const [error, setError]               = useState(null);
    const [picker, setPicker]             = useState({ credentials: [], settingsSchema: [], hotelId: null, accountId: null, partnerId: null });
    const [addLevel, setAddLevel]         = useState(null);            // 'hotel' | 'account' | 'partner' | null
    const [addName, setAddName]           = useState('');
    const [addValues, setAddValues]       = useState({});
    const [submitting, setSubmitting]     = useState(false);
    const [shareTarget, setShareTarget]   = useState('hotel');         // 'hotel' | 'account' (per-level on inline create)
    const [addStep, setAddStep]           = useState(1);               // add wizard: 1 Details · 2 Test · 3 Share
    const [overrideValues, setOverrideValues] = useState({});          // must-override field values — used for the test only, never saved
    const [testPassed, setTestPassed]     = useState(false);
    const [editCred, setEditCred]         = useState(null);            // non-null → wizard is editing this existing credential (update, not create)

    const load = async () => {
        if (!marketplaceId) return;
        setLoading(true); setError(null);
        try {
            const data = await entityMktSettingsService.getPicker({
                marketplaceId, context, contextId, hotelId, accountId, partnerId,
            });
            setPicker(data || { credentials: [], settingsSchema: [] });
        } catch (e) {
            setError(apiErrorMessage(e, 'Failed to load credentials'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [marketplaceId, context, contextId, hotelId, accountId, partnerId]);

    const effective = {
        hotelId:   picker.hotelId   ?? hotelId   ?? null,
        accountId: picker.accountId ?? accountId ?? null,
        partnerId: picker.partnerId ?? partnerId ?? null,
    };

    const targets = useMemo(() => {
        const out = [];
        if (effective.hotelId)   out.push({ value: 'hotel',   label: picker.hotelName   ? `${picker.hotelName} (Hotel)`        : 'this hotel' });
        if (effective.accountId) out.push({ value: 'account', label: picker.accountName ? `${picker.accountName} (Hotel Chain)` : 'this chain' });
        return out;
    }, [effective.hotelId, effective.accountId, picker.hotelName, picker.accountName]);

    // Default share target follows the most-specific available scope (hotel preferred)
    useEffect(() => {
        if (!targets.length) return;
        if (!targets.find(t => t.value === shareTarget)) setShareTarget(targets[0].value);
    }, [targets, shareTarget]);

    // Tabbed layout: open on the most-specific level available (hotel → chain → partner)
    useEffect(() => {
        setActiveTab(effective.hotelId ? 'hotel' : effective.accountId ? 'account' : 'partner');
    }, [effective.hotelId, effective.accountId, effective.partnerId]); // eslint-disable-line

    const isSelected = (c) =>
        selection &&
        c.credential_id != null &&
        Number(selection.credential_id) === Number(c.credential_id);

    const grouped = useMemo(() => {
        const order = { partner: 0, account: 1, hotel: 2 };
        return [...picker.credentials].sort((a, b) =>
            (order[a.entity_type] - order[b.entity_type]) ||
            String(a.credential_name).localeCompare(b.credential_name)
        );
    }, [picker.credentials]);

    // SET PER HOTEL — BUT NOT AT A HOTEL. A must-override field (the OHIP hotel id) is left N/A on a partner or chain
    // credential because each hotel using it supplies its own. A HOTEL-level credential has nothing below it to supply
    // one, so there it is an ordinary field: typed, required and saved. Left N/A there, every call went out as
    // /hotels/N%2FA/… and OHIP answered 404 (Aman Jiwo, AMAN-OHIP-MUMBAI, 2026-10-30).
    const setPerHotel = (s, level) => s.must_be_overridden === 'Y' && level !== 'hotel';

    const startAdd = (level) => {
        setAddLevel(level);
        setAddName('');
        const initial = {};
        picker.settingsSchema.forEach(s => {
            // Fields the marketplace flags as "must be overridden" are supplied per
            // hotel/instance at override time, not on the base credential — default to N/A.
            initial[s.setting_name] = setPerHotel(s, level) ? 'N/A' : '';
        });
        setAddValues(initial);
        setOverrideValues({});
        setEditCred(null);
        setAddStep(1);
        setTestPassed(false);
        setError(null);
    };

    // Superadmin: re-enter the wizard to edit an existing credential's values/name.
    // Prefills from the credential's (already decrypted) settings; must-override
    // fields stay N/A. On finish it updates the credential — sharing is unchanged.
    const startEdit = (cred) => {
        const byName = {};
        (cred.settings || []).forEach(s => { byName[s.setting_name] = s; });
        const initial = {};
        picker.settingsSchema.forEach(s => {
            const saved = byName[s.setting_name]?.setting_value ?? '';
            initial[s.setting_name] = setPerHotel(s, cred.entity_type)
                ? 'N/A'
                // A hotel credential saved while the field was locked holds the placeholder — shown empty, so it is filled.
                : (s.must_be_overridden === 'Y' && String(saved).trim().toUpperCase() === 'N/A' ? '' : saved);
        });
        setAddLevel(cred.entity_type);
        setAddName(cred.credential_name || '');
        setAddValues(initial);
        setOverrideValues({});
        setEditCred(cred);
        setAddStep(1);
        setTestPassed(false);
        setError(null);
    };

    const cancelAdd = () => {
        setAddLevel(null);
        setAddName('');
        setAddValues({});
        setOverrideValues({});
        setEditCred(null);
        setAddStep(1);
        setTestPassed(false);
        setError(null);
    };

    // Step-1 validation: name + all required non-override fields present.
    const validateStep1 = () => {
        if (!addName.trim()) { setError('Name is required'); return false; }
        const missing = picker.settingsSchema.find(s =>
            !setPerHotel(s, addLevel) && s.locked !== 'Y'
            && s.required === 'Y' && !String(addValues[s.setting_name] || '').trim());
        if (missing) { setError(`${missing.setting_label || missing.setting_name} is required`); return false; }
        setError(null);
        return true;
    };

    const submitAdd = async () => {
        if (!addName.trim()) { setError('Name is required'); return; }
        // AN ISSUED FIELD IS NEVER "MISSING". It is filled in by the server on save, so demanding it
        // here would refuse every new MCP credential for the absence of a value only saving creates.
        const requiredMissing = picker.settingsSchema.find(s => s.locked !== 'Y'
            && s.required === 'Y' && !String(addValues[s.setting_name] || '').trim());
        if (requiredMissing) { setError(`${requiredMissing.setting_label || requiredMissing.setting_name} is required`); return; }

        const settings = picker.settingsSchema
            .filter(s => addValues[s.setting_name] !== undefined)
            .map(s => ({ name: s.setting_name, value: addValues[s.setting_name] || '' }));

        // Edit mode — update the existing credential's values/name only; sharing is left untouched.
        if (editCred) {
            setSubmitting(true); setError(null);
            try {
                await entityMktSettingsService.saveCredentials(
                    editCred.entity_type, editCred.entity_id, marketplaceId, addName.trim(), settings, editCred.credential_id
                );
                cancelAdd();
                await load();
            } catch (e) {
                setError(apiErrorMessage(e, 'Failed to update credential'));
            } finally {
                setSubmitting(false);
            }
            return;
        }

        const sourceEntityId = addLevel === 'partner' ? effective.partnerId
                              : addLevel === 'account' ? effective.accountId
                              : effective.hotelId;
        if (!sourceEntityId) { setError(`Cannot add at ${ENTITY_LABEL[addLevel]} level — id missing from context`); return; }

        // The share scope follows the level the credential is added at: a hotel
        // credential is shared with the hotel, a chain credential with the chain.
        // Only a partner credential offers a choice (share down to chain or hotel).
        const shareWithHotel   = addLevel === 'hotel'                              ? effective.hotelId
                                : addLevel === 'partner' && shareTarget === 'hotel' ? effective.hotelId
                                : null;
        const shareWithAccount = addLevel === 'account'                             ? effective.accountId
                                : addLevel === 'partner' && shareTarget === 'account' ? effective.accountId
                                : null;

        setSubmitting(true); setError(null);
        try {
            const result = await entityMktSettingsService.shareAndBind({
                sourceEntityType: addLevel,
                sourceEntityId,
                marketplaceId,
                credentialName: addName.trim(),
                newSettings: settings,
                shareWithHotel, shareWithAccount,
            });
            cancelAdd();
            await load();
            onCreateAndShare && onCreateAndShare({
                shareId: result.shareId,
                credential_id: result.credentialId,
                entity_type: addLevel,
                entity_id: sourceEntityId,
                credential_name: addName.trim(),
                marketplace_id: marketplaceId,
            });
        } catch (e) {
            setError(apiErrorMessage(e, 'Failed to create credential'));
        } finally {
            setSubmitting(false);
        }
    };

    const pickExisting = async (cred) => {
        // When the consumer needs a share_id (sync) — or the cred isn't already shared with this
        // project's hotel/chain — call share-and-bind to ensure a row exists. The backend is
        // idempotent so calling repeatedly is safe.
        const needsShare = requiresShareId
            || (cred.entity_type !== 'hotel' && !cred.is_shared_with_hotel && !cred.is_shared_with_account);
        if (needsShare && (effective.hotelId || effective.accountId)) {
            try {
                const result = await entityMktSettingsService.shareAndBind({
                    sourceEntityType: cred.entity_type,
                    sourceEntityId:   cred.entity_id,
                    marketplaceId,
                    credentialId:     cred.credential_id,
                    // Hotel scope by default (most specific). Hotel-level creds: target the hotel itself.
                    shareWithHotel:   effective.hotelId,
                    shareWithAccount: null,
                });
                onSelect && onSelect({ ...cred, share_id: result.shareId, is_shared_with_hotel: true });
                load();
                return;
            } catch (e) {
                setError(apiErrorMessage(e, 'Failed to share credential'));
                return;
            }
        }
        onSelect && onSelect(cred);
    };

    const renderRow = (c, idx) => (
        <div
            key={`${c.entity_type}-${c.entity_id}-${c.credential_name}-${idx}`}
            className={`cred-picker-row${isSelected(c) ? ' selected' : ''}`}
        >
            <button type="button" className="cred-picker-row-select" onClick={() => pickExisting(c)}>
                <div className="cred-picker-row-main">
                    <span className="cred-picker-row-name">{c.credential_name}</span>
                    <span className={`cred-picker-row-level cred-level-${c.entity_type}`}>{ENTITY_LABEL[c.entity_type]}</span>
                    {(c.is_shared_with_hotel || c.is_shared_with_account) && (
                        <span className="cred-picker-row-shared">Shared</span>
                    )}
                </div>
                <div className="cred-picker-row-meta">
                    {c.entity_name} · {c.settings_count} setting{c.settings_count !== 1 ? 's' : ''}
                </div>
            </button>
            {canEdit && c.credential_id != null && (
                <button type="button" className="cred-picker-row-edit" onClick={() => startEdit(c)}>Edit</button>
            )}
        </div>
    );

    const mustOverrideFields = picker.settingsSchema.filter(s => setPerHotel(s, addLevel));
    const cloudTestable = !!picker.isCloudTestable;

    // Settings payload for the connection test: real values for normal fields, plus
    // the values typed in step 2 for the must-override fields (test-only, not saved).
    const testSettings = picker.settingsSchema.map(s => ({
        name: s.setting_name,
        value: setPerHotel(s, addLevel) ? (overrideValues[s.setting_name] || '') : (addValues[s.setting_name] || ''),
    }));

    // Who will be able to use the credential once created, based on the add level.
    const shareScope = addLevel === 'hotel'      ? (picker.hotelName   ? `${picker.hotelName} only` : 'this hotel only')
                     : addLevel === 'account'    ? (picker.accountName ? `every hotel in ${picker.accountName}` : 'every hotel in this chain')
                     : shareTarget === 'account' ? (picker.accountName ? `every hotel in ${picker.accountName}` : 'every hotel in this chain')
                     :                             (picker.hotelName   ? `${picker.hotelName} only` : 'this hotel only');

    // In edit mode the scope reflects the existing credential (sharing isn't changed here).
    const usageScope = !editCred ? shareScope
                     : editCred.entity_type === 'hotel'   ? (picker.hotelName   ? `${picker.hotelName} only` : 'this hotel only')
                     : editCred.entity_type === 'account' ? (picker.accountName ? `every hotel in ${picker.accountName}` : 'every hotel in this chain')
                     :                                      'the hotels and chains it is shared with';

    const renderSettingInput = (s, values, setValues, onAfterChange) => {
        const set = (val) => { setValues(v => ({ ...v, [s.setting_name]: val })); onAfterChange && onAfterChange(); };
        if (s.data_type === 'PASSWORD') {
            return <input type="password" value={values[s.setting_name] || ''} onChange={e => set(e.target.value)} placeholder={s.placeholder || ''} />;
        }
        if (s.available_values) {
            return (
                <select value={values[s.setting_name] || ''} onChange={e => set(e.target.value)}>
                    <option value="">Select…</option>
                    {s.available_values.split('|').map(pair => {
                        const [key, label] = pair.split(':');
                        return <option key={key} value={key.trim()}>{(label || key).trim()}</option>;
                    })}
                </select>
            );
        }
        return <input type="text" value={values[s.setting_name] || ''} onChange={e => set(e.target.value)} placeholder={s.placeholder || ''} />;
    };

    const addFormEl = addLevel && (
        <div className="cred-picker-add-form">
            <div className="cred-picker-add-header">
                {editCred ? 'Edit credential at ' : 'Add a new credential at '}<strong>{ENTITY_LABEL[addLevel]}</strong> level
            </div>

            <div className="cred-picker-steps">
                {[{ n: 1, label: 'Details' }, { n: 2, label: 'Test' }, { n: 3, label: 'Share' }].map(st => (
                    <span key={st.n} className={`cred-picker-step${addStep === st.n ? ' active' : ''}${addStep > st.n ? ' done' : ''}`}>
                        <span className="cred-picker-step-num">{addStep > st.n ? '✓' : st.n}</span> {st.label}
                    </span>
                ))}
            </div>

            {/* Step 1 — Details */}
            {addStep === 1 && (
                <>
                    <label className="cred-picker-field">
                        <span className="cred-picker-field-label">Name <span className="req">*</span></span>
                        <input type="text" value={addName} onChange={e => setAddName(e.target.value)} placeholder="e.g. Production OHIP" />
                    </label>
                    {picker.settingsSchema.map(s => {
                        const mustOverride = setPerHotel(s, addLevel);
                        // ISSUED, NOT TYPED (V514). The platform generates it — an MCP instance id, the
                        // client id and secret handed to a model — or derives it from the environment.
                        // Shown so it can be read and copied, never edited, and on a NEW credential not
                        // yet shown at all: it does not exist until Save, and an empty box beside five
                        // filled ones reads as something forgotten.
                        const issued = s.locked === 'Y';
                        const issuedValue = issued ? String(addValues[s.setting_name] || '') : '';
                        return (
                            <label key={s.setting_name} className="cred-picker-field">
                                <span className="cred-picker-field-label">
                                    {s.setting_label || s.setting_name}
                                    {!issued && s.required === 'Y' && <span className="req"> *</span>}
                                    {issued && <span className="cred-picker-issued-tag">issued</span>}
                                </span>
                                {mustOverride
                                    ? <input type="text" value="N/A" disabled readOnly />
                                    : issued
                                        ? (issuedValue
                                            ? <IssuedValue value={issuedValue} secret={s.data_type === 'PASSWORD'} />
                                            : <input type="text" value="" disabled readOnly
                                                placeholder="Issued when this credential is saved" />)
                                        : renderSettingInput(s, addValues, setAddValues)}
                                {mustOverride ? (
                                    <span className="cred-picker-field-help">
                                        Set per hotel when the credential is used, not on the base credential — left as N/A here.
                                    </span>
                                ) : s.helper_text ? (
                                    <span className="cred-picker-field-help">{s.helper_text}</span>
                                ) : null}
                            </label>
                        );
                    })}
                    <div className="cred-picker-add-actions">
                        <button type="button" className="cred-picker-cancel" onClick={cancelAdd} disabled={submitting}>Cancel</button>
                        <button type="button" className="cred-picker-save" onClick={() => { if (validateStep1()) setAddStep(2); }}>Next</button>
                    </div>
                </>
            )}

            {/* Step 2 — Test */}
            {addStep === 2 && (
                <>
                    {cloudTestable ? (
                        <>
                            <p className="cred-picker-wizard-intro">
                                Test the connection before sharing.
                                {mustOverrideFields.length > 0 && ' Enter values for the fields that are set per hotel — these run the test only and are not saved on the credential.'}
                            </p>
                            {mustOverrideFields.map(s => (
                                <label key={s.setting_name} className="cred-picker-field">
                                    <span className="cred-picker-field-label">
                                        {s.setting_label || s.setting_name} <span className="cred-picker-field-note">(test only)</span>
                                    </span>
                                    {renderSettingInput(s, overrideValues, setOverrideValues, () => setTestPassed(false))}
                                </label>
                            ))}
                            <CredentialTest
                                marketplaceId={marketplaceId}
                                settings={testSettings}
                                onResult={r => setTestPassed(!!r.success)}
                            />
                        </>
                    ) : (
                        <p className="cred-picker-wizard-intro">
                            This marketplace can’t be tested from the cloud — continue to sharing.
                        </p>
                    )}
                    <div className="cred-picker-add-actions">
                        <button type="button" className="cred-picker-cancel" onClick={() => { setAddStep(1); setTestPassed(false); }}>Back</button>
                        <button type="button" className="cred-picker-save" onClick={() => setAddStep(3)} disabled={cloudTestable && !testPassed}>Next</button>
                    </div>
                </>
            )}

            {/* Step 3 — Share */}
            {addStep === 3 && (
                <>
                    <p className="cred-picker-wizard-intro">
                        This <strong>{ENTITY_LABEL[addLevel]}</strong> credential {editCred ? 'is' : 'will be'} available to <strong>{usageScope}</strong>.
                    </p>
                    {!editCred && addLevel === 'partner' && targets.length > 1 && (
                        <div className="cred-picker-share-target">
                            <span className="cred-picker-field-label">Share with:</span>
                            {targets.map(t => (
                                <label key={t.value} className="cred-picker-share-target-opt">
                                    <input type="radio" checked={shareTarget === t.value}
                                           onChange={() => setShareTarget(t.value)} /> {t.label}
                                </label>
                            ))}
                        </div>
                    )}
                    <div className="cred-picker-add-actions">
                        <button type="button" className="cred-picker-cancel" onClick={() => setAddStep(2)} disabled={submitting}>Back</button>
                        <button type="button" className="cred-picker-save" onClick={submitAdd} disabled={submitting}>
                            {submitting ? 'Saving…' : 'Finish'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );

    // ── Tabbed layout (Partner / Chain / Hotel) — used by the full-screen sync picker ──
    if (tabbed) {
        const TABS = [
            { key: 'partner', label: 'Partner', id: effective.partnerId },
            { key: 'account', label: 'Chain',   id: effective.accountId },
            { key: 'hotel',   label: 'Hotel',   id: effective.hotelId },
        ];
        const rowsFor = (type) => grouped.filter(c => c.entity_type === type);
        const active = TABS.find(t => t.key === activeTab) || TABS[2];
        const canAddHere = allowAddAt.includes(active.key) && !!active.id;

        // Add/Edit runs as its own screen (not inline under the list).
        if (addLevel) {
            return (
                <div className="cred-picker">
                    <button type="button" className="cred-picker-wizard-back" onClick={cancelAdd}>← Back to credentials</button>
                    {error && <div className="cred-picker-error">{error}</div>}
                    {addFormEl}
                </div>
            );
        }

        return (
            <div className="cred-picker">
                {error   && <div className="cred-picker-error">{error}</div>}
                {loading && <div className="cred-picker-status">Loading…</div>}
                {!loading && (
                    <>
                        <div className="cred-picker-tabs">
                            {TABS.map(t => (
                                <button
                                    key={t.key}
                                    type="button"
                                    className={`cred-picker-tab${activeTab === t.key ? ' active' : ''}`}
                                    onClick={() => { setActiveTab(t.key); cancelAdd(); }}
                                >
                                    {t.label}
                                    <span className="cred-picker-tab-count">{rowsFor(t.key).length}</span>
                                </button>
                            ))}
                        </div>
                        <div className="cred-picker-tabpanel">
                            {rowsFor(active.key).length === 0
                                ? <div className="cred-picker-empty">No {active.label.toLowerCase()}-level credentials yet.</div>
                                : <div className="cred-picker-list">{rowsFor(active.key).map(renderRow)}</div>}

                            {canAddHere
                                ? <div className="cred-picker-add-bar"><button type="button" className="cred-picker-add-btn" onClick={() => startAdd(active.key)}>+ Add at {active.label}</button></div>
                                : <div className="cred-picker-empty">Adding a credential isn’t available at this level for this project.</div>}
                        </div>
                    </>
                )}
            </div>
        );
    }

    // Add/Edit runs as its own screen (not inline under the list).
    if (addLevel) {
        return (
            <div className="cred-picker">
                <button type="button" className="cred-picker-wizard-back" onClick={cancelAdd}>← Back to credentials</button>
                {error && <div className="cred-picker-error">{error}</div>}
                {addFormEl}
            </div>
        );
    }

    return (
        <div className="cred-picker">
            {error    && <div className="cred-picker-error">{error}</div>}
            {loading  && <div className="cred-picker-status">Loading…</div>}
            {!loading && (
                <>
                    {grouped.length === 0 && (
                        <div className="cred-picker-empty">No credentials at the partner, chain or hotel level yet.</div>
                    )}
                    <div className="cred-picker-list">
                        {grouped.map(renderRow)}
                    </div>

                    <div className="cred-picker-add-bar">
                        {allowAddAt.includes('partner')  && effective.partnerId && (
                            <button type="button" className="cred-picker-add-btn" onClick={() => startAdd('partner')}>+ Add at Partner</button>
                        )}
                        {allowAddAt.includes('account')  && effective.accountId && (
                            <button type="button" className="cred-picker-add-btn" onClick={() => startAdd('account')}>+ Add at Chain</button>
                        )}
                        {allowAddAt.includes('hotel')    && effective.hotelId && (
                            <button type="button" className="cred-picker-add-btn" onClick={() => startAdd('hotel')}>+ Add at Hotel</button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
